<template>
  <div class="schedule-details">
    <div
      v-if="isSubscribed || (pageSubscribers && pageSubscribers.count !== 0)"
    >
      <p class="text-center text-small text-grey-60 font-weight-semi-bold my-1">
        <span v-if="isSubscribed">{{
          $t("components.pageActions.schedule.scheduleDetails.youText")
        }}</span>
        <template v-if="pageSubscribers">
          <span v-if="isSubscribed && pageSubscribers.count > 0"
            >&nbsp;{{ $t("components.pageActions.schedule.scheduleDetails.and") }}</span
          >
          <span v-if="pageSubscribers.count > 0"
            >&nbsp;{{ pageSubscribers.count }}
            {{ pluralize("Member", pageSubscribers.count) }}</span
          >
          &nbsp;{{
            pageSubscribers.count === 0
              ? $t("components.pageActions.schedule.scheduleDetails.have")
              : !isSubscribed && pageSubscribers.count === 1
              ? $t("components.pageActions.schedule.scheduleDetails.has")
              : $t("components.pageActions.schedule.scheduleDetails.have")
          }}
          {{ $t("components.pageActions.schedule.scheduleDetails.join") }}
        </template>
      </p>

      <div
        class="schedule-details__subscribers-list d-flex justify-content-center"
        data-cy="page-schedule-subscriber-list"
      >
        <subscriber-avatar
          class="schedule-details__subscriber-avatar"
          v-for="(subscriber, i) in subscriberAvatars"
          :key="i"
          :subscriber="subscriber"
          small
        />

        <share>
          <v-button rounded primary>{{
            $t("components.pageActions.schedule.scheduleDetails.invite")
          }}</v-button>
        </share>
      </div>
    </div>

    <div class="schedule-details__time text-center">
      <template v-if="hasFinishedCountdown">
        <span
          >{{ pageType }}
          {{ $t("components.pageActions.schedule.scheduleDetails.hasStarted") }}</span
        >
      </template>
      <template v-else-if="hasEnded">
        <template v-if="schedule.is_all_day && hasEndedRecently">
          <template v-if="isSameDay">
            <span>{{ $t("components.pageActions.schedule.scheduleDetails.endedToday") }}</span>
          </template>
          <template v-else>
            <span>{{ $t("components.pageActions.schedule.scheduleDetails.endedYesterday") }}</span>
          </template>
        </template>
        <template v-else>
          <span>{{ pageType }} {{ $t("components.pageActions.schedule.scheduleDetails.ended") }} {{ timeAgo(schedule.ends_at) }}</span>
        </template>
      </template>
      <template v-else-if="isFuture && isStartingToday && countdownTimer">
        <template v-if="schedule.is_all_day">
          <template v-if="isSameDay">
            <span>{{ $t("components.pageActions.schedule.scheduleDetails.startingToday") }}</span>
          </template>
          <template v-else>
            <span>{{ $t("components.pageActions.schedule.scheduleDetails.startingTomorrow") }}</span>
          </template>
        </template>
        <template v-else>
          <span>{{ $t("components.pageActions.schedule.scheduleDetails.startingIn",{humanTime: countdownTimer.humanTime} ) }}</span>
        </template>
      </template>
      <template v-else-if="schedule.is_all_day">
        <template v-if="isSameDay">
          <span>
            {{ toDate(schedule.starts_at, "dddd MMM D") }}
          </span>
        </template>
        <template v-else>
          <span>
            {{ toDate(schedule.starts_at, "dddd MMM D") }} -
            {{ toDate(schedule.ends_at, "dddd MMM D") }}</span
          >
        </template>
      </template>
      <template v-else>
        <template v-if="hasStarted && countdownTimer">
          <span>{{ $t("components.pageActions.schedule.scheduleDetails.startedAgo",{humanTime: countdownTimer.humanTime} ) }}</span>
        </template>
        <template v-else-if="isSameDay">
          <span>
            {{ toDate(schedule.starts_at, "dddd MMM D") }}
            {{ $t("components.pageActions.schedule.scheduleDetails.at") }} 
            {{ toTime(schedule.starts_at, "h:mmA") }} 
            {{ $t("components.pageActions.schedule.scheduleDetails.for") }}
            {{ timeBetween(schedule.starts_at, schedule.ends_at) }}
          </span>
        </template>
        <template v-else-if="isWithinADay">
          <span>
            {{ toDate(schedule.starts_at, "MMM D") }}
            {{ $t("components.pageActions.schedule.scheduleDetails.at") }} 
            {{ toTime(schedule.starts_at, "h:mmA") }}
            {{ $t("components.pageActions.schedule.scheduleDetails.for") }}
            {{ timeBetween(schedule.starts_at, schedule.ends_at) }}
          </span>
        </template>
        <template v-else>
          <span>
            {{ toDate(schedule.starts_at, "MMM D") }}
            {{ $t("components.pageActions.schedule.scheduleDetails.at") }} 
            {{ toTime(schedule.starts_at, "h:mmA") }} -
            {{ toDate(schedule.ends_at, "MMM D") }} 
            {{ $t("components.pageActions.schedule.scheduleDetails.at") }}
            {{ toTime(schedule.ends_at, "h:mmA") }}
          </span>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex"

import { isPresent, pluralize } from "@/lib/utils"
import {
  toDate,
  isSameDay,
  toTime,
  isPast,
  timeBetween,
  isWithin,
  secondsUntil,
  timeAgo,
  isFuture
} from "@/lib/date-utils"
import Timer, { COUNT_DIRECTIONS, TIMER_EVENTS, FORMATS } from "@/lib/timer"

import SubscriberAvatar from "@/components/SubscriberAvatar"
import Share from "@/components/block-actions/Share"

export default {
  components: {
    Share,
    SubscriberAvatar
  },

  props: {
    pageType: { type: String, required: true },
    pageId: { type: [Number, String], required: true },
    schedule: { type: Object, required: true }
  },

  data() {
    return {
      hasFinishedCountdown: false,
      countdownTimer: null
    }
  },

  methods: {
    toTime,
    toDate,
    timeBetween,
    isPresent,
    pluralize,
    timeAgo
  },

  mounted() {
    if (!this.hasEnded) {
      if (this.hasStarted) {
        this.countdownTimer = new Timer({
          startAt: Math.abs(secondsUntil(this.schedule.starts_at)),
          countDirection: COUNT_DIRECTIONS.UP,
          format: FORMATS.READABLE
        })
        this.countdownTimer.start()
      } else if (this.isStartingToday) {
        this.countdownTimer = new Timer({
          seconds: secondsUntil(this.schedule.starts_at),
          countDirection: COUNT_DIRECTIONS.DOWN,
          format: FORMATS.READABLE
        })
        this.countdownTimer.on(TIMER_EVENTS.FINISHED, () => {
          this.hasFinishedCountdown = true
          this.$emit("countdownEnded")
        })
        this.countdownTimer.start()
      }
    }
  },

  computed: {
    ...mapGetters("pages", ["isSubscribedToPage", "getPageSubscribers"]),
    ...mapGetters("auth", ["currentUser"]),

    isSubscribed() {
      return this.isSubscribedToPage(this.pageId)
    },

    pageSubscribers() {
      return this.getPageSubscribers(this.pageId)
    },

    subscriberAvatars() {
      const subscribers = isPresent(this.pageSubscribers)
        ? this.pageSubscribers.last_5
        : []

      return this.isSubscribed
        ? [...subscribers, this.currentUser]
        : subscribers
    },

    isFuture() {
      return isFuture(this.schedule.starts_at)
    },

    isSameDay() {
      // TODO: Update to isWithin to prevent weirdness when duration causes endsAt to be the next day
      return isSameDay(this.schedule.starts_at, this.schedule.ends_at)
    },

    hasStarted() {
      return this.isSameDay
        ? isPast(this.schedule.starts_at)
        : isSameDay(this.schedule.starts_at, new Date()) ||
            isPast(this.schedule.starts_at)
    },

    hasEnded() {
      return isPast(this.schedule.ends_at)
    },

    isStartingToday() {
      return isWithin(this.schedule.starts_at, 1, "day")
    },

    hasEndedRecently() {
      return isWithin(this.schedule.ends_at, 1, "day")
    },

    isWithinADay() {
      return isWithin(this.schedule.starts_at, 1, "day", this.schedule.ends_at)
    },

    attendingCount() {
      return this.isSubscribed
        ? this.pageSubscribers.count + 1
        : this.pageSubscribers.count
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.schedule-details__row {
  animation: fade-in 0.6s ease;
}

.schedule-details__subscriber-avatar {
  animation: fade-in-up 0.3s ease;
  margin-right: -0.65rem;
  border: 2px solid $color-ui--white;
}

.schedule-details__time {
  color: $color-ui--grey-80;
  font-weight: $font-weight--semi-bold;
  padding: $size--3 0 $size--3;
  margin-bottom: $size--2;
}

.schedule-details__subscribers-list {
  .v-button {
    border: 2px solid $color-ui--white;
  }
}
</style>
